import { useRef, useEffect, useMemo } from "react";
import { X, RotateCcw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import QuickReplies, { getContextualReplies } from "./QuickReplies";
import { useGlobalSettings } from "@/hooks/useGlobalSettings";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp?: Date;
}

interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  messages: Message[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  onClearChat: () => void;
  isBlocked?: boolean;
  onQuickReplyClick?: (label: string, message: string) => void;
}

const ChatWindow = ({
  isOpen,
  onClose,
  messages,
  isLoading,
  onSendMessage,
  onClearChat,
  isBlocked = false,
  onQuickReplyClick,
}: ChatWindowProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { data: settings } = useGlobalSettings();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Get contextual quick replies based on conversation
  const quickReplies = useMemo(() => {
    if (isBlocked) return [];
    return getContextualReplies(messages);
  }, [messages, isBlocked]);
  
  // Show quick replies after assistant messages (not while loading)
  const lastMessage = messages[messages.length - 1];
  const showQuickReplies = !isLoading && !isBlocked && (!lastMessage || lastMessage.role === "assistant");

  const handleQuickReplySelect = (message: string, label: string) => {
    if (onQuickReplyClick) {
      onQuickReplyClick(label, message);
    }
    onSendMessage(message);
  };

  return (
    <div
      className={cn(
        "fixed bottom-24 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl bg-background border shadow-2xl transition-all duration-300 overflow-hidden",
        isOpen
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-4 pointer-events-none"
      )}
      style={{ maxHeight: "min(600px, calc(100vh - 140px))" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
            <span className="text-lg">💬</span>
          </div>
          <div>
            <h3 className="font-semibold text-sm">
              {settings?.site_name || "Chat with us"}
            </h3>
            <p className="text-xs opacity-80">We typically reply instantly</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClearChat}
            className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
            title="Start new conversation"
            aria-label="Start new conversation"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
            aria-label="Close chat"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Blocked Banner */}
      {isBlocked && (
        <div className="px-4 py-2 bg-destructive/10 border-b border-destructive/20 flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Chat temporarily restricted. Please contact us directly.</span>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: isBlocked ? "280px" : "320px" }}>
        {messages.map((message, index) => (
          <ChatMessage 
            key={message.id} 
            message={message} 
            isTyping={isLoading && index === messages.length - 1 && message.role === "assistant"}
          />
        ))}

        {isLoading && messages[messages.length - 1]?.role === "user" && (
          <ChatMessage
            message={{ id: "typing", role: "assistant", content: "", timestamp: new Date() }}
            isTyping={true}
          />
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Replies - contextual based on conversation */}
      {showQuickReplies && quickReplies.length > 0 && (
        <div className="px-4 pb-3 border-t border-border/50 pt-3">
          <QuickReplies
            replies={quickReplies}
            onSelect={handleQuickReplySelect}
            isLoading={isLoading}
          />
        </div>
      )}

      {/* Input */}
      <ChatInput onSend={onSendMessage} isLoading={isLoading} disabled={isBlocked} />
    </div>
  );
};

export default ChatWindow;
